import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Header, AnnouncementBar } from "./Header";
import { Footer } from "./Footer";
import { TrustBar } from "./TrustBar";
import { WhatsAppFab } from "./WhatsAppFab";
import { MiniCart } from "./MiniCart";
import { Intro } from "./Intro";
import { AmbientAudio } from "./AmbientAudio";
import { Container } from "./Primitives";
import { AnimatePresence, EASE_OUT, motion } from "../lib/motion";

const COOKIE_KEY = "sme_cookie_choice";

function readChoice() {
  const m = document.cookie.match(new RegExp("(^| )" + COOKIE_KEY + "=([^;]+)"));
  return m ? m[2] : null;
}

export function CookieBanner() {
  const [choice, setChoice] = useState(readChoice);

  const set = (v) => {
    document.cookie = `${COOKIE_KEY}=${v};path=/;max-age=31536000;SameSite=Lax`;
    setChoice(v);
  };

  return (
    <AnimatePresence>
      {!choice && (
        <motion.div
          role="dialog"
          aria-label="Cookie choices"
          data-testid="cookie-banner"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-rule bg-surface/[0.98] backdrop-blur-xl"
        >
          <Container className="flex flex-col gap-5 py-6 md:flex-row md:items-center">
            <p className="ui flex-1 text-xs leading-[1.7] text-dim">
              Essential cookies keep your cart, session and currency choice working. Analytics cookies
              are off until you say yes. We do not use advertising or cross-site tracking cookies.{" "}
              <Link to="/legal/cookies" className="link-u">Cookie policy</Link>
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="cookie-reject-all"
                onClick={() => set("reject")}
                className="btn btn-secondary flex-1 md:flex-none"
                style={{ minWidth: 132 }}
              >
                Reject all
              </button>
              <button
                type="button"
                data-testid="cookie-accept-all"
                onClick={() => set("accept")}
                className="btn btn-secondary flex-1 md:flex-none"
                style={{ minWidth: 132 }}
              >
                Accept all
              </button>
            </div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Layout({ children }) {
  const location = useLocation();
  const showTrustBar = location.pathname.startsWith("/shop");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-wash">
      <div className="grain-overlay" aria-hidden="true" />
      <a
        href="#main"
        className="ui sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-deep focus:px-4 focus:py-2 focus:text-wash"
      >
        Skip to content
      </a>
      <AnnouncementBar />
      <Header />
      {showTrustBar && <TrustBar />}
      <main id="main" className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.34, ease: EASE_OUT }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
      <WhatsAppFab />
      <MiniCart />
      <AmbientAudio />
      <CookieBanner />
      <Intro />
    </div>
  );
}
